import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";

import {
  getRetrieveRetailProductsFaqsQueryKey,
  retrieveRetailProductsFaqs,
} from "@/api/orval/retail-products/retail-products";
import { useTranslation } from "@/core/i18n";
import ArrowLeft from "atoms/icon/icons/arrow_left.svg";
import faqsData from "constants/faqs.json";
import { AccordionItem, AccordionList } from "molecules/accordion-list/AccordionList";
import TopNav from "molecules/navbars/TopNav";

import { queryClient } from "../../Root";

export interface FAQCategory {
  question: string;
  answer: string;
}

interface LoaderData {
  faqs: FAQCategory[];
}

export const FaqsLoader = async () => {
  try {
    const apiFaqs = await queryClient.fetchQuery({
      queryKey: getRetrieveRetailProductsFaqsQueryKey(),
      queryFn: () => retrieveRetailProductsFaqs(),
    });

    if (apiFaqs && apiFaqs.faqs && Array.isArray(apiFaqs.faqs) && apiFaqs.faqs.length > 0) {
      const transformedFaqs: FAQCategory[] = apiFaqs.faqs.map((faq) => ({
        question: faq.question.text,
        answer: faq.answer.text,
      }));

      return {
        faqs: transformedFaqs,
      };
    }
  } catch (error) {
    console.error("Error fetching FAQs from API:", error);
  }

  return {
    faqs: faqsData.faqs,
  };
};

export const shippedOrdersFaqs: FAQCategory[] = [
  {
    question: "When will I receive my order?",
    answer: "Most orders are delivered to you within 3 - 5 business days.",
  },
  {
    question: "How can I track my order?",
    answer:
      "You'll receive a tracking link once your order ships. You can follow your shipment's progress right from your account.",
  },
  {
    question: "Can I change my shipping address after ordering?",
    answer:
      "Orders are processed quickly, so address changes aren't always possible. Please contact us immediately to see if we can update it.",
  },
  {
    question: "How do I cancel my order?",
    answer:
      "We typically pack and ship your order within minutes. Please contact us immediately to see if we are able to cancel your order. In the event that we are unable to, you can request a return on receipt.",
  },
  {
    question: "What is your return policy?",
    answer:
      "We will be happy to accept returns on most unused merchandise within 30 days of receipt. Unfortunately and without exception, we cannot accept returns on used or opened items - as we guarantee the purity, freshness, and authenticity of the products we sell.  We reserve the right to deny a return or issue a store credit for any reason.\n\nWe accept to up to three individual items to be returned in a 365-day period. After three returns, you may make another once 365 days have passed from the date each individual return was processed.\n\nPre-Paid shipping labels will be provided only for those who receive incorrect or damaged items. All customers are responsible for their own return shipment charges. If an item is purchased in error, we cannot be responsible for the return shipment charges. We highly recommend using a shipping service with a tracking number and insurance. We cannot be responsible for lost packages without proof of delivery.\n\nReturns require 5-7 business days to process once received by our Returns Department.\n\nAny original shipping fees are non-refundable. We are not responsible for return packages that may be lost or damaged in transit; we recommend you retain your shipping receipt for proof.\n\nIf you have any questions about your order, please do not hesitate to contact us.\n\nWe reserve the right to change and update this return policy at any time.",
  },
  {
    question: "What if my package arrives damaged or missing items?",
    answer:
      "Please contact us within 48 hours of delivery. We'll make it right as quickly as possible.",
  },
  {
    question: "Where do I receive my products from?",
    answer:
      "We partner with SkinDrop by RepeatMD as a reliable source of dermatologist approved skincare products.",
  },
];

export default function FaqsPage() {
  const { t } = useTranslation();
  const { faqs } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hasMyProductParam = searchParams.get("hasMyProduct");
  const hasSkinDropParam = searchParams.get("hasSkinDrop");

  // If no parameters are passed, show both sections
  const hasMyProduct =
    hasMyProductParam === "true" || (hasMyProductParam === null && hasSkinDropParam === null);
  const hasSkinDrop =
    hasSkinDropParam === "true" || (hasMyProductParam === null && hasSkinDropParam === null);

  const createAccordionItems = (faqList: FAQCategory[]): AccordionItem[] =>
    faqList.map((faq) => ({
      title: faq.question,
      children: (
        <div className="whitespace-pre-line text-justify text-body3 text-primary">{faq.answer}</div>
      ),
      titleClassName: "text-bold2",
    }));

  const generalAccordionItems = createAccordionItems(faqs);
  const shippedAccordionItems = createAccordionItems(shippedOrdersFaqs);

  return (
    <div className="min-h-screen bg-two desk:mx-auto desk:max-w-3xl desk:py-6">
      <TopNav
        startIconSVG={ArrowLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
      >
        {t("faqsPage.header")}
      </TopNav>

      {hasMyProduct && (
        <div className="mb-8">
          <div className="bg-two px-4 py-2">
            <h2 className="text-heading3 text-center font-semibold">
              {t("faqsPage.pickupOrdersTitle")}
            </h2>
          </div>
          <AccordionList items={generalAccordionItems} className="bg-white" />
        </div>
      )}

      {hasSkinDrop && (
        <div className="mb-8">
          <div className="bg-two px-4 pb-2">
            <h2 className="text-heading3 text-center font-semibold">
              {t("faqsPage.ordersShippedTitle")}
            </h2>
          </div>
          <AccordionList items={shippedAccordionItems} className="bg-white" />
        </div>
      )}
    </div>
  );
}
